import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
  EmailLessLogin,
  PhoneLessLogin,
  resendOTP,
} from "../../redux/actions/AuthUserActions";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../components/loader/Loader";
import FormalityHeader from "../../components/header/FormalityHeader";
import "../styles/flow-form.css";

function FormalityOTP() {
  const navigate = useNavigate();
  const location = useLocation();
  const cart = useSelector((state) => state.cart);
  const [otp, setOtp] = useState(["", "", "", "", ""]);
  const inputs = useRef([]);
  const [resendTimer, setResendTimer] = useState(60);
  const { email, phone, method } = location.state || {};
  const dispatch = useDispatch();
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (value, index) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const newOtp = [...otp];
    newOtp[index] = digit;
    setOtp(newOtp);

    if (digit && index < otp.length - 1) {
      inputs.current[index + 1].focus();
    }
  };

  // Pasting the whole code fills every box at once
  const handlePaste = (e) => {
    const digits = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, otp.length);

    if (!digits) return;
    e.preventDefault();

    const newOtp = otp.map((_, index) => digits[index] || "");
    setOtp(newOtp);
    inputs.current[Math.min(digits.length, otp.length - 1)].focus();
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputs.current[index - 1].focus();
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (resendTimer > 0) {
      const interval = setInterval(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearInterval(interval);
    }
  }, [resendTimer]);

  useEffect(() => {
    if (otp.every((digit) => digit !== "")) {
      const otpCode = otp.join("");
      setSubmitting(true);
      if (method === "email") {
        dispatch(
          EmailLessLogin({ email, otp: Number(otpCode) }, handleCallback),
        );
      } else {
        dispatch(
          PhoneLessLogin({ phone, otp: Number(otpCode) }, handleCallback),
        );
      }
    }
  }, [otp]);

  const handleCallback = ({ success, message, companies }) => {
    const Toast = Swal.mixin({
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 4000,
    });
    if (success) {
      switch (cart.from) {
        case "crimRecord":
          navigate("/birth-place");
          break;

        case "certificateNationality":
          navigate("/certificate-nationality-country");
          break;

        case "certificateSingleness":
          navigate("/certificate-singleness-country");
          break;

        case "eneo":
          navigate("/eneo-country");
          break;

        case "customsRebate":
          navigate("/customs-rebate-country");
          break;

        case "businessPlan":
          navigate("/business-plan-sector-activity");
          break;

        case "visualIdentity":
          navigate("/visual-identity");
          break;

        case "importation":
          if (Array.isArray(companies) && companies.length > 0) {
            navigate("/importation-companies");
          } else {
            navigate("/importation-business-country");
          }
          break;

        case "oapi":
          if (Array.isArray(companies) && companies.length > 0) {
            navigate("/oapi-companies");
          } else {
            navigate("/oapi-business-country");
          }
          break;

        case "passport":
          navigate("/passport-country");
          break;

        case "trademark":
          navigate("/trademark");
          break;

        case "taxRegistration":
          navigate("/tax-registration");
          break;

        case "taxCompliance":
          navigate("/tax-compliance");
          break;

        case "cnpsRegistration":
          navigate("/cnps-registration");
          break;

        default:
          // Rien à faire ici
          break;
      }
    } else {
      Toast.fire({
        icon: "error",
        title: "Échec",
        text: message,
      });
      // Lets the user type the code again
      setOtp(["", "", "", "", ""]);
      inputs.current[0]?.focus();
    }
    setSubmitting(false);
  };

  const resendCode = () => {
    setSubmitting(true);
    // New accounts get their code by e-mail and by SMS
    dispatch(resendOTP({ email, channel: "both" }, handleCodeCallback));
  };

  const handleCodeCallback = ({ success, message }) => {
    const Toast = Swal.mixin({
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 4000,
    });
    if (success) {
      Toast.fire({
        icon: "success",
        title: "Succès",
        text: message,
      });
      setResendTimer(60);
    } else {
      Toast.fire({
        icon: "error",
        title: "Échec",
        text: message,
      });
    }
    setSubmitting(false);
  };

  return (
    <div className="flow-form-page">
      <Loader isLoading={submitting} />
      <FormalityHeader from={cart.from} />

      <header className="flow-form-header">
        <h1 className="flow-form-title">
          Saisissez votre <span>code</span>
        </h1>
        <p className="flow-form-lead">
          Nous venons de vous envoyer un code à 5 chiffres par e-mail
          {email ? (
            <>
              {" "}
              à <strong>{email}</strong>
            </>
          ) : null}{" "}
          et par SMS sur votre téléphone. Il est valable 30 minutes.
        </p>
      </header>

      <div className="flow-form">
        <div
          className="otp-inputs"
          role="group"
          aria-label="Code de vérification"
        >
          {otp.map((digit, index) => (
            <input
              key={index}
              type="text"
              inputMode="numeric"
              autoComplete={index === 0 ? "one-time-code" : "off"}
              className={`flow-input otp-input ${digit ? "is-filled" : ""}`}
              aria-label={`Chiffre ${index + 1}`}
              value={digit}
              onChange={(e) => handleChange(e.target.value, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              onPaste={handlePaste}
              ref={(el) => (inputs.current[index] = el)}
              autoFocus={index === 0}
            />
          ))}
        </div>

        <p className="otp-resend">
          {resendTimer > 0 ? (
            <>Nouveau code disponible dans {resendTimer} s</>
          ) : (
            <button type="button" onClick={resendCode}>
              Recevoir un nouveau code
            </button>
          )}
        </p>
      </div>
    </div>
  );
}

export default FormalityOTP;
